import { useEffect, useState } from 'react';

import { UserCheck01Icon, UserIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { formatCheckStatus, formatDateForDisplay } from 'helpers/candidate-portal-helpers';
import { useToast } from 'hooks/toast';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Accordion } from 'components/Accordion/Accordion';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import {
  Button,
  Email,
  Input,
  InputGroup,
  PhoneNumberPicker,
  Textarea,
} from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';
import { UserAvatar } from 'components/UserAvatar/UserAvatar';

import { useUpdateRefereeMutation } from 'api/candidate-portal-api';

const REFEREE_FORM_FIELDS = ['first_name', 'last_name', 'email', 'contact_number'];

const DESKTOP_QUERY = '(min-width: 640px)';

// Some Tailwind utility combinations in this project don't override `hidden` responsively,
// so the desktop/mobile action layouts are switched in JS instead of via `hidden sm:flex`.
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const handleChange = event => setIsDesktop(event.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
};

const RefereeForm = ({
  defaultValues,
  onSubmit,
  submitLabel,
  country,
  compareEmails = [],
  compareContacts = [],
}) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const onFormSubmit = data => {
    const params = REFEREE_FORM_FIELDS.reduce((acc, field) => {
      acc[field] = typeof data[field] === 'string' ? data[field].trim() : data[field];
      return acc;
    }, {});
    params.reason = data.reason || '';
    onSubmit(params);
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onFormSubmit)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InputGroup title={t('candidatePortal.referees.fields.firstName')}>
          <Input name="first_name" register={register} validators={{ required: true }} />
        </InputGroup>
        <InputGroup title={t('candidatePortal.referees.fields.lastName')}>
          <Input name="last_name" register={register} validators={{ required: true }} />
        </InputGroup>
      </div>
      <InputGroup title={t('candidatePortal.referees.fields.email')}>
        <Email
          name="email"
          register={register}
          validators={{ required: true }}
          compareEmailValidation
          compareEmail={compareEmails}
          compareErrorMessage={t('candidatePortal.referees.fields.emailCompareErrorMessage')}
          error={errors?.email?.message}
        />
      </InputGroup>
      <InputGroup title={t('candidatePortal.referees.fields.contactNumber')}>
        <Controller
          control={control}
          name="contact_number"
          rules={{
            validate: value =>
              !compareContacts.includes(value) ||
              t('candidatePortal.referees.fields.contactCompareErrorMessage'),
          }}
          render={({ field: { onChange, value } }) => (
            <PhoneNumberPicker
              value={value}
              onChange={onChange}
              defaultCountry={country}
              error={errors?.contact_number}
            />
          )}
        />
      </InputGroup>
      <InputGroup title={t('candidatePortal.referees.fields.reason')}>
        <Textarea
          name="reason"
          register={register}
          rows={3}
          validators={{ required: t('validation.required') }}
          error={errors?.reason?.message}
          placeholder={t('candidatePortal.referees.fields.reasonPlaceholder')}
        />
      </InputGroup>
      <Button submit className="w-full mt-2">
        {submitLabel}
      </Button>
    </form>
  );
};

const RefereeRow = ({
  referee,
  nominationUrl,
  onEdit,
  onReplace,
  replaced = false,
  isBypassed = false,
}) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const isCompleted = !!referee.completed_at;
  const isOverdue = referee.status === 'overdue';
  const dateColorClass = isCompleted
    ? 'text-text-success'
    : isOverdue
      ? 'text-text-error'
      : 'text-text-secondary';
  const dateLabel = isCompleted
    ? `${t('candidatePortal.submitted')}: ${formatDateForDisplay(referee.completed_at)}`
    : `${t('candidatePortal.referees.requested')}: ${formatDateForDisplay(referee.submitted_at)}`;

  const initials = `${referee.first_name?.[0] || ''}${referee.last_name?.[0] || ''}`.toUpperCase();

  const isNominated = !!referee.submitted_at;
  const canModify = referee.is_editable && isNominated && !isBypassed;
  const displayName = isNominated ? referee.name : t('candidatePortal.referees.nominationPending');
  const canNominate =
    !replaced && !isNominated && referee.is_editable && !isBypassed && !!nominationUrl;

  const handleActionClick = key => {
    if (key === 'edit') {
      onEdit(referee);
    } else if (key === 'replace') {
      onReplace(referee);
    } else if (key === 'nominate') {
      window.open(nominationUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={`flex flex-col ${replaced ? 'bg-neutral-3' : 'bg-surface-primary'} border border-border-primary rounded-2xl w-full`}
      role="listitem">
      <div className="flex items-center gap-4 p-4">
        <div className="flex gap-3 items-center flex-1 min-w-0">
          <UserAvatar background="neutral">
            {isNominated ? (
              <span>{initials}</span>
            ) : (
              <HugeiconsIcon icon={UserIcon} size={18} strokeWidth={2} />
            )}
          </UserAvatar>
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <div className="flex gap-2 items-center flex-wrap">
              <span
                className={` text-2xl ${replaced ? 'line-through text-neutral-6' : 'text-neutral-7'}`}>
                {displayName}
              </span>
              {!replaced && (
                <StatusPill
                  status={referee.status}
                  text={formatCheckStatus(referee.status)}
                  small
                  noBorder
                  className="px-4"
                />
              )}
            </div>
            {isNominated && (
              <div className="flex gap-2 items-center flex-wrap text-xs">
                <span className="text-text-tertiary text-xl">{referee.email}</span>
                {referee.role && (
                  <>
                    <span className="text-text-tertiary">•</span>
                    <span className="text-text-tertiary text-xl">{referee.role}</span>
                  </>
                )}
                <span className="text-text-tertiary">•</span>
                <span className={`text-text-tertiary text-xl ${dateColorClass}`}>{dateLabel}</span>
              </div>
            )}
          </div>
        </div>
        {canNominate && isDesktop && (
          <div className="flex gap-2 shrink-0">
            <Button
              type="secondary-reverse"
              small
              icon="external-link"
              edged
              light
              onClick={() => handleActionClick('nominate')}>
              {t('candidatePortal.referees.nominate')}
            </Button>
          </div>
        )}
        {canModify && isDesktop && (
          <div className="flex gap-2 shrink-0">
            <Button
              type="secondary-reverse"
              small
              icon="edit"
              edged
              light
              onClick={() => onEdit(referee)}>
              {t('candidatePortal.referees.edit')}
            </Button>
            <Button
              type="secondary-reverse"
              small
              icon="refresh-cw"
              edged
              light
              onClick={() => onReplace(referee)}>
              {t('candidatePortal.referees.replace')}
            </Button>
          </div>
        )}
      </div>
      {canNominate && !isDesktop && (
        <div className="border-t border-border-primary w-full">
          <ButtonGroup
            actions={[
              {
                key: 'nominate',
                label: t('candidatePortal.referees.nominate'),
                icon: 'external-link',
              },
            ]}
            onClick={handleActionClick}
          />
        </div>
      )}
      {canModify && !isDesktop && (
        <div className="border-t border-border-primary w-full">
          <ButtonGroup
            actions={[
              { key: 'edit', label: t('candidatePortal.referees.edit'), icon: 'edit' },
              { key: 'replace', label: t('candidatePortal.referees.replace'), icon: 'refresh-cw' },
            ]}
            onClick={handleActionClick}
          />
        </div>
      )}
    </div>
  );
};

export const CandidateReferencesCard = ({ check, company, candidate, brand = {} }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useToast();
  const [editingReferee, setEditingReferee] = useState(null);

  const [updateReferee] = useUpdateRefereeMutation();

  const referees = check?.referees || [];
  const activeReferees = referees.filter(referee => !referee.replaced_by);
  const replacedReferees = referees.filter(referee => referee.replaced_by);
  const countedReferees = activeReferees.filter(referee => !referee.canceled && !referee.deleted);
  const completedCount = countedReferees.filter(referee => referee.status === 'completed').length;

  const getCompareValues = excludedRefereeId => {
    const otherActiveReferees = activeReferees.filter(
      referee => referee.id !== excludedRefereeId && !referee.canceled && !referee.deleted,
    );
    return {
      compareEmails: [
        candidate?.email,
        ...otherActiveReferees.map(referee => referee.email),
      ].filter(Boolean),
      compareContacts: [
        candidate?.contact_number,
        ...otherActiveReferees.map(referee => referee.contact_number),
      ].filter(Boolean),
    };
  };

  const handleUpdate = async params => {
    try {
      await updateReferee({ refereeId: editingReferee.id, params }).unwrap();
      setEditingReferee(null);
      showToast(
        t('candidatePortal.referees.updateSuccessTitle'),
        t('candidatePortal.referees.updateSuccessMessage'),
        'success',
      );
    } catch (error) {
      showToast(
        t('candidatePortal.referees.errorTitle'),
        error?.data?.error?.log || t('candidatePortal.referees.updateErrorMessage'),
        'error',
      );
    }
  };

  const handleReplace = referee => {
    navigate(`/portal/dashboard/${candidate.id}/referees/${referee.id}/replace`);
  };

  const header = (
    <div className="flex gap-4 items-center">
      <IconComponent
        icon={UserCheck01Icon}
        className={
          brand?.color_button && !check.canceled
            ? `!bg-[var(--brand-badge-bg)] !text-[var(--brand-text)] !border-0`
            : check.canceled
              ? '!border-0 opacity-50'
              : ''
        }
        type={brand?.color_button ? 'secondary' : ''}
        aria-hidden="true"
      />
      <div className="flex-1 flex flex-col">
        <h3 className="text-2xl font-medium text-neutral-7 inter-font m-0">
          {t('candidatePortal.referees.title')}
        </h3>
        <div className="flex gap-2 items-center flex-wrap text-xs">
          <p className="text-xl text-text-tertiary font-light m-0">
            {t('candidatePortal.referees.completedCount', {
              completed: completedCount,
              total: countedReferees.length,
            })}
          </p>
          {replacedReferees.length > 0 && <span className="text-text-tertiary">•</span>}
          {replacedReferees.length > 0 && (
            <p className="text-xl text-text-tertiary font-light m-0">
              {' '}
              {t('candidatePortal.referees.replacedCount', {
                count: replacedReferees.length,
              })}{' '}
            </p>
          )}
        </div>
      </div>
      <StatusPill
        status={check.status}
        text={formatCheckStatus(check.status)}
        small
        noBorder
        className="px-4"
      />
    </div>
  );

  return (
    <section className="bg-surface-primary border border-border-primary rounded-3xl shadow-sm p-6 w-full">
      <Accordion header={header} defaultCollapsed={false}>
        <div
          className="flex flex-col gap-4 bg-surface-secondary border border-border-primary rounded-2xl p-4 w-full"
          role="list"
          aria-label={t('candidatePortal.referees.title')}>
          {activeReferees.map(referee => (
            <RefereeRow
              nominationUrl={check?.nomination_url}
              isBypassed={check?.details?.bypassed || false}
              key={referee.id}
              referee={referee}
              onEdit={setEditingReferee}
              onReplace={handleReplace}
            />
          ))}
        </div>

        {replacedReferees.length > 0 && (
          <div className="mt-4">
            <Accordion
              defaultCollapsed
              header={
                <p className="text-xl text-text-tertiary font-light m-0">
                  {t('candidatePortal.referees.replacedTitle')}
                </p>
              }>
              <div
                className="flex flex-col gap-4 bg-surface-secondary border border-border-primary rounded-2xl p-4 w-full"
                role="list"
                aria-label={t('candidatePortal.referees.replacedSectionTitle')}>
                {replacedReferees.map(referee => (
                  <RefereeRow
                    key={referee.id}
                    referee={referee}
                    onEdit={setEditingReferee}
                    onReplace={handleReplace}
                    replaced
                  />
                ))}
              </div>
            </Accordion>
          </div>
        )}
      </Accordion>

      <AnimatedModal
        visible={!!editingReferee}
        onClose={() => setEditingReferee(null)}
        title={t('candidatePortal.referees.editModalTitle')}
        small
        oldIconLibrary={false}>
        {editingReferee && (
          <RefereeForm
            defaultValues={{
              ...REFEREE_FORM_FIELDS.reduce((acc, field) => {
                acc[field] = editingReferee[field] || '';
                return acc;
              }, {}),
              reason: '',
            }}
            onSubmit={handleUpdate}
            submitLabel={t('candidatePortal.referees.saveChanges')}
            country={company?.country}
            {...getCompareValues(editingReferee.id)}
          />
        )}
      </AnimatedModal>
    </section>
  );
};
